import { forwardRef, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx';

export type SwitchProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'children'> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Visible label; clicking it toggles the switch. */
  label: ReactNode;
  description?: ReactNode;
  /** Label before the switch (settings rows) or after it. */
  labelPosition?: 'start' | 'end';
};

/**
 * An on/off toggle for settings that apply immediately (never for something that needs a confirm).
 * role="switch" with aria-checked; on fills with the selected colour, off is an outlined well.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, description, labelPosition = 'start', disabled, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const switchId = id ?? `sw${auto.replace(/:/g, '')}`;
  const text = (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-sm text-jc-ink">{label}</span>
      {description ? <span className="text-sm text-jc-ink-secondary">{description}</span> : null}
    </span>
  );
  return (
    <label
      htmlFor={switchId}
      className={cx(
        'inline-flex items-center gap-3',
        labelPosition === 'start' && 'justify-between',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        className,
      )}
    >
      {labelPosition === 'start' ? text : null}
      <SwitchControl
        {...rest}
        ref={ref}
        id={switchId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
      />
      {labelPosition === 'end' ? text : null}
    </label>
  );
});

export type SwitchControlProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'children'> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** The switch itself, without a label: for rows that are the label (ListRow's toggle). Name it with a <label htmlFor> or aria-labelledby. */
export const SwitchControl = forwardRef<HTMLButtonElement, SwitchControlProps>(function SwitchControl(
  { checked, onCheckedChange, disabled, className, ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cx(
        'relative inline-flex h-8 w-13 shrink-0 items-center rounded-jc-control p-0.5 transition-colors duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
        'disabled:cursor-not-allowed',
        checked ? 'bg-jc-selected' : 'bg-jc-elevated ring-1 ring-inset ring-jc-border-control',
        className,
      )}
    >
      <span
        aria-hidden
        className={cx(
          'size-7 rounded-full shadow-jc-sm transition-transform duration-(--jc-duration-base) ease-jc-out motion-reduce:transition-none',
          checked ? 'translate-x-5 bg-jc-on-selected' : 'translate-x-0 bg-jc-logo-surface',
        )}
      />
    </button>
  );
});
